/**
 * Name sanity warnings (DBS-263, Cycle 4 Q4.1.2).
 *
 * Heuristics for the two data-entry mistakes the DBS asks about: a value in the wrong name field, and
 * the same value entered more than once. They are WARNINGS, never blocking: every pattern here has a
 * real-world exception (51 applicants in the Aug 2026 data have forename = surname), so the applicant,
 * the countersignatory and the confirm screen are shown the flag and left to decide.
 *
 * Part of the consolidated rule set (DBS-344): copied verbatim into the portals by api/scripts/sync_dbs_rules.sh.
 */

/** The name fields, as a structural type so every repo's data shape fits without imports */
export type NameFields = {
  Title?: string | null,
  Forename?: string | null,
  Middlenames?: { Middlename?: (string | null | undefined)[] | null } | null,
  PresentSurname?: string | null,
  AdditionalApplicantDetails?: {
    BirthSurname?: string | null,
    OtherForenames?: { OtherForename?: ({ Name?: string | null } | null | undefined)[] | null } | null,
    OtherSurnames?: { OtherSurname?: ({ Name?: string | null } | null | undefined)[] | null } | null,
  } | null,
}

export type NameWarning = {
  /** Which field the applicant should look at */
  field: string,
  message: string,
}

/** The honorifics from the e-Bulk TitleType enum (the country codes that share the enum are not titles) */
export const TITLE_TOKENS = [
  'MISS', 'MR', 'MRS', 'MS', 'BARON', 'BARONESS', 'BRIGADIER', 'CANON', 'CAPTAIN', 'DR', 'DUCHESS', 'DUKE', 'ESQ',
  'FATHER', 'HON', 'INSPECTOR', 'LADY', 'LORD', 'MAJOR', 'PASTOR', 'PROFESSOR', 'RABBI', 'REVEREND', 'SIR', 'SISTER',
]

const norm = (value: string | null | undefined): string => (value ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
const words = (value: string): string[] => value.split(' ').filter(w => w.length > 0)

/** Finds the first word that immediately repeats itself, e.g. "SMITH SMITH" → "SMITH" */
const repeatedWord = (value: string): string | undefined => {
  const ws = words(value)
  for (let i = 1; i < ws.length; i++) if (ws[i] === ws[i - 1]) return ws[i]
  return undefined
}

/** A title on its own, or leading the value ("MR", "MR JOHN") */
const leadingTitle = (value: string): string | undefined => {
  const first = words(value)[0]
  return first !== undefined && TITLE_TOKENS.includes(first) ? first : undefined
}

export const findNameWarnings = (names: NameFields): NameWarning[] => {
  const warnings: NameWarning[] = []
  const add = (field: string, message: string) => warnings.push({ field, message })

  const forename = norm(names.Forename)
  const surname = norm(names.PresentSurname)
  const middlenames = (names.Middlenames?.Middlename ?? []).map(norm).filter(m => m.length > 0)
  const birthSurname = norm(names.AdditionalApplicantDetails?.BirthSurname)
  const otherForenames = (names.AdditionalApplicantDetails?.OtherForenames?.OtherForename ?? []).map(o => norm(o?.Name)).filter(n => n.length > 0)
  const otherSurnames = (names.AdditionalApplicantDetails?.OtherSurnames?.OtherSurname ?? []).map(o => norm(o?.Name)).filter(n => n.length > 0)

  // Every single field: a title where a name should be, or a word typed twice
  const singleFields: [string, string][] = [
    ['Forename', forename],
    ...middlenames.map((m, i): [string, string] => [middlenames.length === 1 ? 'Middlename' : `Middlename ${i + 1}`, m]),
    ['Present Surname', surname],
    ['Birth Surname', birthSurname],
    ...otherForenames.map((n, i): [string, string] => [`Other Forename ${i + 1}`, n]),
    ...otherSurnames.map((n, i): [string, string] => [`Other Surname ${i + 1}`, n]),
  ]
  for (const [field, value] of singleFields) {
    if (value.length === 0) continue
    const title = leadingTitle(value)
    if (title !== undefined) add(field, `${field} starts with the title "${title}". Titles belong in the Title field, not in a name.`)
    const repeat = repeatedWord(value)
    if (repeat !== undefined) add(field, `${field} repeats the word "${repeat}". Please check it has not been typed twice.`)
  }

  // Across fields: the same value in two places usually means one of them is in the wrong field
  if (forename.length > 0 && forename === surname) {
    add('Forename', `Forename and Present Surname are both "${forename}". Please check the right name is in each field.`)
  }
  middlenames.forEach((m, i) => {
    const label = middlenames.length === 1 ? 'Middlename' : `Middlename ${i + 1}`
    if (m === forename) add(label, `${label} "${m}" is the same as the Forename. Please check it is really a middle name.`)
    else if (m === surname) add(label, `${label} "${m}" is the same as the Present Surname. Please check it is really a middle name.`)
    else if (middlenames.indexOf(m) < i) add(label, `${label} "${m}" is entered more than once.`)
  })
  otherForenames.forEach((n, i) => {
    if (n === forename) add(`Other Forename ${i + 1}`, `Other Forename "${n}" is the same as the current Forename. Other names are only for names no longer used.`)
    else if (otherForenames.indexOf(n) < i) add(`Other Forename ${i + 1}`, `Other Forename "${n}" is entered more than once.`)
  })
  otherSurnames.forEach((n, i) => {
    if (n === surname) add(`Other Surname ${i + 1}`, `Other Surname "${n}" is the same as the Present Surname. Other names are only for names no longer used.`)
    else if (otherSurnames.indexOf(n) < i) add(`Other Surname ${i + 1}`, `Other Surname "${n}" is entered more than once.`)
  })

  return warnings
}
